import { z } from 'zod';

export const bool = z
    .boolean()
    .or(z.enum(['true', 'false', '']))
    .transform(value => {
        try {
            if (typeof value === 'boolean') {
                return value;
            }

            return value ? (JSON.parse(value) as boolean) : undefined;
        } catch {
            return undefined;
        }
    });

export const envSchema = z.object({
    VITE_MODE: z.enum(['development', 'production']),
    VITE_PROD: bool,
    VITE_DEV: bool,

    VITE_WEBSITE_NAME: z.string().optional(),
    VITE_CLIENT_ID: z.string(),
    VITE_ACCEPTED_DOMAIN: z.string().optional(),
    VITE_API: z.string().url(),
    VITE_CLIENT_DOMAIN: z.string().optional(),
    VITE_DEMO: bool.optional().default(false),
    VITE_SAAS: bool.optional().default(false),

    /**
     * If true, Google Analytics will be enabled.
     */
    VITE_IS_GA_ENABLED: bool.optional().default(false),

    VITE_IS_SENTRY_ENABLED: bool.optional().default(false),
    VITE_SENTRY_ENVIRONMENT: z
        .enum([
            /**
             * E.g. https://ackee-dev.passwd.team
             */
            'development',

            /**
             * E.g. https://ackee.passwd.team
             */
            'stage',

            /**
             * E.g. https://app.passwd.team
             */
            'production',

            '',
        ])
        // CI will set it to empty string, rather than undefined
        .transform(value => (value === '' ? undefined : value))
        .optional(),

    /**
     * A store URL of the Edge extension.
     */
    VITE_EDGE_EXT_URL: z
        .string()
        .url()
        .default('https://microsoftedge.microsoft.com/addons/detail/passwd-team-password-man/mpcdhhelipgmahhfknbpjkgendnmbpcb'),

    /**
     * A store URL of the Chrome extension.
     */
    VITE_CHROME_EXT_URL: z
        .string()
        .url()
        .default('https://chromewebstore.google.com/detail/passwd-team-password-mana/mnngnbmkiabhobdmeolcnjidongapoba'),

    /**
     * A store URL of the Firefox extension.
     */
    VITE_FIREFOX_EXT_URL: z.string().url().default('https://addons.mozilla.org/en-US/firefox/addon/passwd-team-password-manager'),

    /**
     * A store URL of the Safari extension.
     */
    VITE_SAFARI_EXT_URL: z.string().url().default('https://apps.apple.com/us/app/passwd-for-safari/id6788817220'),
    VITE_STRIPE_LINK: z.string().url(),
    VITE_THEME_COLORS: z.string().optional().default('{}'),
    VITE_LOGO_URL: z.string().optional(),
    VITE_USES_IAP: bool.optional().default(false),

    /**
     * Enable logger, dev modes, etc.
     * @default false
     */
    VITE_LOCALHOST: bool.optional().default(false),
    VITE_BEAR_AUTH_DEBUG_LOGS: bool.optional().default(false),

    /**
     * If true, the service worker will be enabled.
     * @default true
     */
    VITE_SERVICE_WORKER: bool.optional().default(true),

    /**
     * @default false
     */
    VITE_PLAYWRIGHT_RUN: bool.optional().default(false),

    /**
     * If true, admin will see all secret records, but he won't be able to see the sensitive fields of secret records.
     * Nor will he be able to impersonate any user.
     * @default false
     */
    VITE_LIMIT_ADMIN_PERMISSIONS: bool.optional().default(false),
});

function isPlaywrightRun() {
    return import.meta.env ? import.meta.env?.VITE_PLAYWRIGHT_RUN : process.env.VITE_PLAYWRIGHT_RUN;
}

/**
 * Allow to override the env var via window.IS_DEMO_PLAYWRIGHT from Playwright tests
 */
function isDemo() {
    if (isPlaywrightRun() && globalThis.window?.IS_DEMO_PLAYWRIGHT) {
        return true;
    }

    return import.meta.env ? import.meta.env?.VITE_DEMO : process.env.VITE_DEMO;
}

/**
 * Allow to override the env var via window.IS_SAAS_PLAYWRIGHT from Playwright tests
 */
function isSaas() {
    if (isPlaywrightRun() && globalThis.window?.IS_SAAS_PLAYWRIGHT) {
        return true;
    }

    return import.meta.env ? import.meta.env?.VITE_SAAS : process.env.VITE_SAAS;
}

function getModeEnvs() {
    const MODE = import.meta.env ? import.meta.env?.VITE_MODE : process.env.VITE_MODE;

    return {
        VITE_MODE: MODE,
        VITE_PROD: MODE === 'production',
        VITE_DEV: MODE === 'development',
    } as const;
}

/**
 * Playwright uses process.env, Vite uses import.meta.env.
 */
const getEnvVar = (key: string) => {
    if (import.meta.env?.[key]) {
        return import.meta.env[key];
    }

    if (typeof process !== 'undefined' && process.env?.[key]) {
        return process.env[key];
    }

    return undefined;
};

export const env = envSchema.parse({
    ...getModeEnvs(),

    VITE_WEBSITE_NAME: getEnvVar('VITE_WEBSITE_NAME'),
    VITE_CLIENT_ID: getEnvVar('VITE_CLIENT_ID'),
    VITE_ACCEPTED_DOMAIN: getEnvVar('VITE_ACCEPTED_DOMAIN'),
    VITE_API: getEnvVar('VITE_API'),
    VITE_CLIENT_DOMAIN: getEnvVar('VITE_CLIENT_DOMAIN'),
    VITE_DEMO: isDemo(),
    VITE_SAAS: isSaas(),
    VITE_IS_GA_ENABLED: getEnvVar('VITE_IS_GA_ENABLED'),
    VITE_IS_SENTRY_ENABLED: getEnvVar('VITE_IS_SENTRY_ENABLED'),
    VITE_SENTRY_ENVIRONMENT: getEnvVar('VITE_SENTRY_ENVIRONMENT'),
    VITE_EDGE_EXT_URL: getEnvVar('VITE_EDGE_EXT_URL'),
    VITE_CHROME_EXT_URL: getEnvVar('VITE_CHROME_EXT_URL'),
    VITE_FIREFOX_EXT_URL: getEnvVar('VITE_FIREFOX_EXT_URL'),
    VITE_SAFARI_EXT_URL: getEnvVar('VITE_SAFARI_EXT_URL'),
    VITE_STRIPE_LINK: getEnvVar('VITE_STRIPE_LINK'),
    VITE_THEME_COLORS: getEnvVar('VITE_THEME_COLORS'),
    VITE_LOGO_URL: getEnvVar('VITE_LOGO_URL'),
    VITE_USES_IAP: getEnvVar('VITE_USES_IAP'),
    VITE_LOCALHOST: getEnvVar('VITE_LOCALHOST'),
    VITE_SERVICE_WORKER: getEnvVar('VITE_SERVICE_WORKER'),
    VITE_PLAYWRIGHT_RUN: isPlaywrightRun(),
    VITE_LIMIT_ADMIN_PERMISSIONS: getEnvVar('VITE_LIMIT_ADMIN_PERMISSIONS'),
    VITE_BEAR_AUTH_DEBUG_LOGS: getEnvVar('VITE_BEAR_AUTH_DEBUG_LOGS'),
});

if (env.VITE_LOCALHOST && env.VITE_DEV) {
    console.debug('Loaded env from import.meta.env:', JSON.stringify(env, null, 2));
}
